import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TreeNode } from 'primeng/api';

import { MainFacade } from '../../../main.facade';
import { IFileInfo } from '../../../models/file-info';
import { CvAiRequest, ProcessName, StorageKey } from '../../../models/request';
import { OperationDescriptor, PipelineStep } from '../../../models/processing-pipeline';
import { EngineRegistryService } from '../../../../services/processing-pipeline/engine-registry.service';
import { VISUALIZER, IVisualizer } from '@jax-data-science/sci-image-visualizer';
import { SubmissionStateService } from '../../../../services/submission-state.service';
import { ProcessComponentBase } from '../process.component.base';

/** Operation IDs for which diagram regions can be forwarded as input prompts. */
const REGION_AWARE_OPS = new Set([
  'sam-auto-segment',
  'image-segmentation',
  'object-detection',
]);

@Component({
  selector: 'image-processing',
  templateUrl: './image-processing.component.html',
  styleUrls: ['./image-processing.component.scss'],
})
export class ImageProcessingComponent extends ProcessComponentBase implements OnInit, OnDestroy {

  steps: PipelineStep[] = [];
  selectedStepIndex = -1;

  availableOperations: OperationDescriptor[] = [];
  operationsByCategory: Map<string, OperationDescriptor[]> = new Map();
  operationToAdd: OperationDescriptor | null = null;

  private selectedRelPath: string | undefined;
  private nodeSubscription!: Subscription;
  private validationSubscription!: Subscription;

  constructor(
    mainFacade: MainFacade,
    submissionState: SubmissionStateService,
    private registry: EngineRegistryService,
    @Inject(VISUALIZER) private plotService: IVisualizer,
    private fb: FormBuilder,
  ) {
    super(ProcessName.CVAI, submissionState, mainFacade);
  }

  ngOnInit(): void {
    this.availableOperations = this.registry.getAllOperations()
      .filter(op => op.engineId === 'opencv' || op.engineId === 'transformers-js');
    this.operationsByCategory = this.buildCategoryMap(this.availableOperations);

    this.form = this.fb.group({
      outputSuffix: ['_cvai', Validators.required],
      editOutputSuffix: [false],
    });
    this.persistForm();

    this.nodeSubscription = this.mainFacade.getSelectedNode$().subscribe({
      next: (node: TreeNode<IFileInfo> | null) => {
        if (!node?.data) {
          this.bucket = undefined;
          this.selectedRelPath = undefined;
        } else {
          this.bucket = node.data.project?.bucket;
          this.path = node.leaf ? node.parent?.data?.relPath : node.data?.relPath;
          this.selectedRelPath = node.data.relPath;
        }
      },
    });

    this.validationSubscription = this.mainFacade.getValidationMessage$().subscribe({
      next: (msg) => { this.validationMessage = msg; },
    });
  }

  ngOnDestroy(): void {
    this.nodeSubscription?.unsubscribe();
    this.validationSubscription?.unsubscribe();
  }

  // --- Step management ---

  addOperation(): void {
    if (!this.operationToAdd) return;
    const defaults: Record<string, any> = {};
    for (const p of this.operationToAdd.params) {
      defaults[p.id] = p.defaultValue;
    }
    if (REGION_AWARE_OPS.has(this.operationToAdd.id)) {
      defaults.useRegions = false;
    }
    this.steps = [...this.steps, {
      operationId: this.operationToAdd.id,
      engineId: this.operationToAdd.engineId,
      params: defaults,
      enabled: true,
    }];
    this.operationToAdd = null;
    this.selectedStepIndex = this.steps.length - 1;
  }

  removeStep(index: number): void {
    this.steps = this.steps.filter((_, i) => i !== index);
    if (this.selectedStepIndex >= this.steps.length) {
      this.selectedStepIndex = this.steps.length - 1;
    }
  }

  moveStep(index: number, direction: -1 | 1): void {
    const newIdx = index + direction;
    if (newIdx < 0 || newIdx >= this.steps.length) return;
    const copy = [...this.steps];
    [copy[index], copy[newIdx]] = [copy[newIdx], copy[index]];
    this.steps = copy;
    this.selectedStepIndex = newIdx;
  }

  selectStep(index: number): void {
    this.selectedStepIndex = index;
  }

  toggleStep(index: number): void {
    this.steps[index] = { ...this.steps[index], enabled: !this.steps[index].enabled };
  }

  onParamChange(paramId: string, value: any): void {
    if (this.selectedStepIndex < 0) return;
    const step = this.steps[this.selectedStepIndex];
    this.steps[this.selectedStepIndex] = {
      ...step,
      params: { ...step.params, [paramId]: value },
    };
  }

  // --- Helpers ---

  get selectedStep(): PipelineStep | null {
    return this.selectedStepIndex >= 0 && this.selectedStepIndex < this.steps.length
      ? this.steps[this.selectedStepIndex]
      : null;
  }

  getDescriptor(step: PipelineStep): OperationDescriptor | undefined {
    return this.availableOperations.find(
      o => o.id === step.operationId && o.engineId === step.engineId
    );
  }

  getOperationName(step: PipelineStep): string {
    return this.getDescriptor(step)?.name || step.operationId;
  }

  getEngineBadge(engineId: string): string {
    return engineId === 'transformers-js' ? 'Transformers' : 'OpenCV';
  }

  getEngineBadgeClass(engineId: string): string {
    return engineId === 'transformers-js' ? 'badge-transformers' : 'badge-opencv';
  }

  isRegionAware(step: PipelineStep): boolean {
    return REGION_AWARE_OPS.has(step.operationId);
  }

  hasRegions(): boolean {
    return this.plotService.getRegionPolygons().length > 0;
  }

  get enabledStepCount(): number {
    return this.steps.filter(s => s.enabled).length;
  }

  // --- Submit ---

  onSubmit(): void {
    const request = this.buildRequest();
    if (request) super.submit(request);
  }

  onCreateRule(): void {
    const request = this.buildRequest();
    if (request) super.createRule(request);
  }

  private buildRequest(): CvAiRequest | null {
    if (this.enabledStepCount === 0) return null;
    const request = new CvAiRequest();
    request.outputSuffix = this.form.get('outputSuffix')?.value || '_cvai';
    request.operations = this.steps
      .filter(s => s.enabled)
      .map(s => ({
        engine: s.engineId === 'transformers-js' ? 'transformers' : s.engineId,
        operationId: s.operationId,
        params: this.serializeParams(s),
      }));

    if (this.bucket && this.selectedRelPath) {
      const key = new StorageKey();
      key.bucket = this.bucket;
      key.object = this.selectedRelPath;
      request.inputPaths = [key];
    }

    return request;
  }

  private serializeParams(step: PipelineStep): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(step.params)) {
      if (k === 'useRegions') continue;
      out[k] = String(v);
    }
    if (step.params.useRegions && REGION_AWARE_OPS.has(step.operationId)) {
      const regions = this.plotService.getRegionPolygons();
      if (regions.length > 0) {
        out.input_regions = JSON.stringify(regions);
      }
    }
    return out;
  }

  private buildCategoryMap(ops: OperationDescriptor[]): Map<string, OperationDescriptor[]> {
    const map = new Map<string, OperationDescriptor[]>();
    for (const op of ops) {
      const list = map.get(op.category) ?? [];
      list.push(op);
      map.set(op.category, list);
    }
    return map;
  }
}
